import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Power, RefreshCw } from 'lucide-react';
import Paginacao, { POR_PAGINA } from '../../Paginacao';

// =====================================================================
// Compras — Catálogo da Central de Pedidos (gestão).
// Tela reformulada (pedido da Julia, 2026-07-03):
//   - TABELA do catálogo atual (busca + filtros por fornecedor/status);
//   - INCLUIR peça na mão (MANUAL=1 — protegida da desativação do re-upload,
//     já que nunca está na planilha da central);
//   - ATIVAR/DESATIVAR peça individualmente (desativada some dos comboboxes
//     do Diagnóstico; nunca se apaga — histórico de compras referencia);
//   - re-upload da planilha da central (mesmo importador da carga inicial).
// =====================================================================

const API = '/api/compras';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const inputStyle = { padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const thStyle = { padding: '10px 8px', textAlign: 'left', fontSize: 12, fontWeight: 800, color: '#fff', whiteSpace: 'nowrap' };
const tdStyle = { padding: '8px', fontSize: 13, color: '#334155', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };

const ComprasCatalogo = () => {
  const [catalogo, setCatalogo] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  // Filtros da tabela
  const [busca, setBusca] = useState('');
  const [fFornecedor, setFFornecedor] = useState('');
  const [fStatus, setFStatus] = useState('ativas'); // ativas | desativadas | todas
  const [pagina, setPagina] = useState(1);
  // Formulário de inclusão manual
  const [incluindo, setIncluindo] = useState(false);
  const [nova, setNova] = useState({ codigo: '', descricao: '', fornecedor: '', familia: '', unidade: 'UN', preco: '' });
  const [salvando, setSalvando] = useState(false);
 

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const r = await fetch(`${API}/catalogo?todas=1`, { headers: auth() });
      if (!r.ok) throw new Error((await r.json())?.message || 'Erro ao carregar o catálogo.');
      setCatalogo(await r.json());
    } catch (e) { setErro(e.message); }
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const fornecedores = [...new Set(catalogo.map((c) => c.FORNECEDOR).filter(Boolean))].sort();

  const semAcento = (s) => [...String(s || '').normalize('NFD')]
    .filter((ch) => ch.charCodeAt(0) < 0x300 || ch.charCodeAt(0) > 0x36f)
    .join('').toLowerCase();

  const filtradas = catalogo.filter((c) => {
    if (fStatus === 'ativas' && !c.ATIVO) return false;
    if (fStatus === 'desativadas' && c.ATIVO) return false;
    if (fFornecedor && c.FORNECEDOR !== fFornecedor) return false;
    if (busca) {
      const q = semAcento(busca);
      if (!semAcento(c.DESCRICAO).includes(q) && !semAcento(c.CODIGO).includes(q) && !semAcento(c.FAMILIA).includes(q)) return false;
    }
    return true;
  });
  const visiveis = filtradas.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  const alternarAtivo = async (peca) => {
    setAviso(''); setErro('');
    try {
      const r = await fetch(`${API}/catalogo/${peca.ID}/ativo`, {
        method: 'PUT', headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ativo: peca.ATIVO ? 0 : 1 }),
      });
      if (!r.ok) throw new Error((await r.json())?.message || 'Erro ao atualizar.');
      // Atualiza em memória — sem recarregar 840 linhas por 1 clique.
      setCatalogo((arr) => arr.map((c) => (c.ID === peca.ID ? { ...c, ATIVO: peca.ATIVO ? 0 : 1 } : c)));
    } catch (e) { setErro(e.message); }
  };

  const incluir = async () => {
    setSalvando(true); setErro(''); setAviso('');
    try {
      const r = await fetch(`${API}/catalogo`, {
        method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' }, body: JSON.stringify(nova),
      });
      if (!r.ok) throw new Error((await r.json())?.message || 'Erro ao incluir.');
      setAviso(`Peça "${nova.descricao}" incluída.`);
      setNova({ codigo: '', descricao: '', fornecedor: '', familia: '', unidade: 'UN', preco: '' });
      setIncluindo(false);
      carregar();
    } catch (e) { setErro(e.message); }
    setSalvando(false);
  };


  const setN = (patch) => setNova((n) => ({ ...n, ...patch }));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Catálogo Central de Pedidos</h2>
        <button onClick={carregar} title="Atualizar" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: '#fffbeb', color: '#d97706', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
          <RefreshCw size={15} /> Atualizar
        </button>
        <div style={{ flex: 1 }} />
        <button onClick={() => { setIncluindo(!incluindo); setAviso(''); }} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
          background: '#e3a81d', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
        }}><Plus size={16} /> Incluir peça</button>
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
        {catalogo.filter((c) => c.ATIVO).length} peças ativas · {catalogo.filter((c) => !c.ATIVO).length} desativadas.
        Peça desativada some das listas do Diagnóstico — o histórico de compras não é afetado.
      </p>

      {/* Inclusão manual — a peça fica marcada como MANUAL e não é desativada pelo re-upload */}
      {incluindo && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <strong style={{ fontSize: 14, color: '#0f172a' }}>Nova peça (inclusão manual)</strong>
          <p style={{ margin: '4px 0 12px', fontSize: 12, color: '#64748b' }}>
            Para peça que não vem na planilha da central. Ela fica protegida: o re-upload da planilha não a desativa.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 150px 170px 80px 100px', gap: 8, alignItems: 'end' }}>
            <label style={{ fontSize: 12, color: '#64748b' }}>Código*
              <input style={{ ...inputStyle, display: 'block', width: '100%' }} value={nova.codigo} onChange={(e) => setN({ codigo: e.target.value })} /></label>
            <label style={{ fontSize: 12, color: '#64748b' }}>Descrição*
              <input style={{ ...inputStyle, display: 'block', width: '100%' }} value={nova.descricao} onChange={(e) => setN({ descricao: e.target.value })} /></label>
            <label style={{ fontSize: 12, color: '#64748b' }}>Fornecedor*
              <input style={{ ...inputStyle, display: 'block', width: '100%' }} value={nova.fornecedor} onChange={(e) => setN({ fornecedor: e.target.value })} /></label>
            <label style={{ fontSize: 12, color: '#64748b' }}>Família
              <input style={{ ...inputStyle, display: 'block', width: '100%' }} value={nova.familia} onChange={(e) => setN({ familia: e.target.value })} /></label>
            <label style={{ fontSize: 12, color: '#64748b' }}>Unidade
              <input style={{ ...inputStyle, display: 'block', width: '100%' }} value={nova.unidade} onChange={(e) => setN({ unidade: e.target.value })} /></label>
            <label style={{ fontSize: 12, color: '#64748b' }}>Preço (R$)
              <input type="number" min="0" step="0.01" style={{ ...inputStyle, display: 'block', width: '100%' }} value={nova.preco} onChange={(e) => setN({ preco: e.target.value })} /></label>
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button disabled={salvando || !nova.codigo.trim() || !nova.descricao.trim() || !nova.fornecedor.trim()} onClick={incluir} style={{
              padding: '8px 20px', background: '#e3a81d', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700,
              cursor: 'pointer', opacity: (!nova.codigo.trim() || !nova.descricao.trim() || !nova.fornecedor.trim()) ? 0.5 : 1,
            }}>{salvando ? 'Salvando…' : 'Salvar peça'}</button>
            <button onClick={() => setIncluindo(false)} style={{ padding: '8px 12px', background: '#fff', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>Cancelar</button>
          </div>
        </div>
      )}

      {erro && <p style={{ color: '#b91c1c', fontSize: 14, fontWeight: 600 }}>{erro}</p>}
      {aviso && <p style={{ color: '#166534', fontSize: 14, fontWeight: 600 }}>{aviso}</p>}

      {/* Filtros da tabela */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input style={{ ...inputStyle, flex: 1, minWidth: 200 }} placeholder="Buscar por código, descrição ou família…"
          value={busca} onChange={(e) => { setBusca(e.target.value); setPagina(1); }} />
        <select value={fFornecedor} onChange={(e) => { setFFornecedor(e.target.value); setPagina(1); }} style={inputStyle}>
          <option value="">Fornecedor (todos)</option>
          {fornecedores.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={fStatus} onChange={(e) => { setFStatus(e.target.value); setPagina(1); }} style={inputStyle}>
          <option value="ativas">Ativas</option>
          <option value="desativadas">Desativadas</option>
          <option value="todas">Todas</option>
        </select>
      </div>

      {/* Tabela do catálogo */}
      {carregando ? <p style={{ color: '#94a3b8' }}>Carregando catálogo…</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #e3a81d 0%, #d97706 100%)' }}>
                <th style={thStyle}>Código</th>
                <th style={thStyle}>Descrição</th>
                <th style={thStyle}>Fornecedor</th>
                <th style={thStyle}>Família</th>
                <th style={thStyle}>Un.</th>
                <th style={thStyle}>Preço ref.</th>
                <th style={thStyle}>Origem</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map((c) => (
                <tr key={c.ID} style={{ background: c.ATIVO ? '#fff' : '#f8fafc', color: c.ATIVO ? undefined : '#94a3b8' }}>
                  <td style={tdStyle}><b>{c.CODIGO}</b></td>
                  <td style={{ ...tdStyle, opacity: c.ATIVO ? 1 : 0.6 }}>{c.DESCRICAO}</td>
                  <td style={tdStyle}>{c.FORNECEDOR}</td>
                  <td style={tdStyle}>{c.FAMILIA || '—'}</td>
                  <td style={tdStyle}>{c.UNIDADE || '—'}</td>
                  <td style={tdStyle}>{c.PRECO_REF != null ? `R$ ${Number(c.PRECO_REF).toFixed(2)}` : '—'}</td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: c.MANUAL ? '#ede9fe' : '#f1f5f9', color: c.MANUAL ? '#6d28d9' : '#64748b' }}>
                      {c.MANUAL ? 'Manual' : 'Planilha'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: c.ATIVO ? '#d1fae5' : '#fee2e2', color: c.ATIVO ? '#059669' : '#991b1b' }}>
                      {c.ATIVO ? 'Ativa' : 'Desativada'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <button onClick={() => alternarAtivo(c)} title={c.ATIVO ? 'Desativar (some das listas do Diagnóstico)' : 'Reativar'} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', fontSize: 12, fontWeight: 700,
                      background: '#fff', color: c.ATIVO ? '#991b1b' : '#059669',
                      border: `1px solid ${c.ATIVO ? '#fecaca' : '#a7f3d0'}`, borderRadius: 6, cursor: 'pointer',
                    }}><Power size={13} /> {c.ATIVO ? 'Desativar' : 'Reativar'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Paginacao total={filtradas.length} pagina={pagina} onMudar={setPagina} />
        </div>
      )}

    </div>
  );
};

export default ComprasCatalogo;
